import { DynamicFieldData } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { DynamicFormDefinition } from '@/shared/components/DynamicForm/models/DynamicFormSection'
import { FormFieldDefaultValue, FormFieldSelectOptions, getDynamicFormFields } from '@/shared/lib/getDynamicFormFields'

const defaultDriverDetailFields: DynamicFieldData[] = [
  {
    fieldType: 'text',
    fieldName: 'login',
    label: 'Login',
    placeholder: 'Enter Login',
    defaultValue: '',
    required: true,
    config: {
      required: 'This field is required',
    },
  },
  {
    fieldType: 'text',
    fieldName: 'name',
    label: 'Name',
    placeholder: 'Enter Driver Name',
    defaultValue: '',
    required: true,
    config: {
      required: 'This field is required',
    },
  },
  {
    fieldType: 'text',
    fieldName: 'employeeNumber',
    label: 'Employee#',
    placeholder: 'Driver\'s Employee Number',
    defaultValue: '',
  },
  {
    fieldType: 'text',
    fieldName: 'email',
    label: 'Email',
    placeholder: 'Enter Driver\'s Email',
    defaultValue: '',
    config: {
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
  },
  {
    fieldType: 'text',
    fieldName: 'mobile',
    label: 'Mobile',
    placeholder: 'Enter Driver\'s Mobile Number',
    defaultValue: '',
  },
  {
    fieldType: 'text',
    fieldName: 'managerEmail',
    label: 'Manager Email',
    placeholder: 'Enter Driver Manager\'s Email',
    defaultValue: '',
    config: {
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
  },
  {
    fieldType: 'multiselect',
    fieldName: 'groupIds',
    label: 'Groups',
    placeholder: 'Select Groups',
    defaultValue: [],
    options: [],
    required: true,
    config: {
      required: 'Please select at least one group',
    },
  },
  {
    fieldType: 'multiselect',
    fieldName: 'runIds',
    label: 'Runs',
    placeholder: 'Select Runs',
    defaultValue: [],
    options: [],
    required: true,
    config: {
      required: 'Please select at least one run',
    },
  },
]

const passwordFields: DynamicFieldData[] = [
  {
    fieldType: 'password',
    fieldName: 'password',
    label: 'Password',
    placeholder: 'Enter Password (numbers only)',
    defaultValue: '',
    required: true,
    config: {
      required: 'Password is required',
      pattern: {
        value: /^\d{1,12}$/,
        message: 'Password must be numbers only',
      },
      minLength: {
        value: 4,
        message: 'Password must be at least 4 digits',
      },
      maxLength: {
        value: 100,
        message: 'Password cannot exceed 100 digits',
      },
    },
  },
  {
    fieldType: 'password',
    fieldName: 'confirmPassword',
    label: 'Confirm Password',
    placeholder: 'Re-Enter Password',
    defaultValue: '',
    required: true,
    config: {
      required: 'Password confirmation is required',
      pattern: {
        value: /^\d{1,12}$/,
        message: 'Password must be numbers only',
      },
      minLength: {
        value: 4,
        message: 'Password must be at least 4 digits',
      },
      maxLength: {
        value: 100,
        message: 'Password cannot exceed 100 digits',
      },
      validate: (value: string, formValues: Record<string, unknown>) =>
        value === formValues.password || 'Passwords do not match',
    },
  },
]

export const initialDriverFormValues = {
  login: '',
  name: '',
  employeeNumber: '',
  email: '',
  mobile: '',
  managerEmail: '',
  groupIds: [],
  runIds: [],
  sendEmailOnScheduled: false,
  sendEmailOnCompletion: false,
  isActive: true,
}

type GetDriverFormDefinitionArgs = {
  defaultValues?: FormFieldDefaultValue
  selectOptions?: FormFieldSelectOptions
  isResetPassword?: boolean
  showPasswordFields?: boolean
  isReadOnly?: boolean
}

export const getDriverFormDefinition = ({
  defaultValues,
  selectOptions,
  isResetPassword = false,
  showPasswordFields = false,
  isReadOnly = false,
}: GetDriverFormDefinitionArgs): DynamicFormDefinition => {
  // When resetting password, disable all non-password fields
  const driverFields = defaultDriverDetailFields.map(field => ({
    ...field,
    disabled: isReadOnly || isResetPassword,
  }))

  const fields = getDynamicFormFields({
    fields: [
      ...driverFields,
      ...((isResetPassword || showPasswordFields) ? passwordFields : []),
    ],
    defaultValues,
    selectOptions,
  })

  const formDefinition: DynamicFormDefinition = {
    formType: 'default',
    sections: [
      {
        label: 'Driver Details',
        fields,
      },
    ],
  }

  return formDefinition
}